import React, { useState, useRef, useEffect } from 'react';
import QrScanner from 'qr-scanner';
import { db, auth } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { userService } from './services/userService';

const QRScanner = ({ onClose }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [error, setError] = useState('');
  const [importedData, setImportedData] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.stop();
        qrScannerRef.current.destroy();
      }
    };
  }, []);

  const parseVCard = (vcard) => {
    const lines = vcard.split('\n');
    const data = {};
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (trimmedLine.startsWith('FN:')) {
        data['姓名'] = trimmedLine.replace('FN:', '');
      } else if (trimmedLine.startsWith('ORG:')) {
        data['公司名稱'] = trimmedLine.replace('ORG:', '');
      } else if (trimmedLine.startsWith('TITLE:')) {
        data['職位'] = trimmedLine.replace('TITLE:', '');
      } else if (trimmedLine.startsWith('TEL:')) {
        data['電話'] = trimmedLine.replace('TEL:', '');
      } else if (trimmedLine.startsWith('EMAIL:')) {
        data['Email'] = trimmedLine.replace('EMAIL:', '');
      } else if (trimmedLine.startsWith('URL:')) {
        data['網站'] = trimmedLine.replace('URL:', '');
      } else if (trimmedLine.startsWith('ADR:')) {
        // ADR format: ;;street;city;state;postal;country
        const address = trimmedLine.replace('ADR:', '').split(';').join(' ').trim();
        if (address) {
          data['地址'] = address;
        }
      }
    });
    
    return data;
  };

  const startScanning = async () => {
    try {
      setError('');
      setIsScanning(true);
      
      if (!videoRef.current) {
        throw new Error('Video element not found');
      }

      qrScannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          setScannedData(result.data);
          
          // 嘗試解析 vCard
          if (result.data.includes('BEGIN:VCARD')) {
            const parsedData = parseVCard(result.data);
            setImportedData(parsedData);
          } else {
            // 如果不是 vCard，可能是純文字或其他格式
            setImportedData({ '掃描內容': result.data });
          }
          
          stopScanning();
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment'
        }
      );

      await qrScannerRef.current.start();
    } catch (err) {
      console.error('QR Scanner error:', err);
      setError(`掃描啟動失敗: ${err.message}`);
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
    }
    setIsScanning(false);
  };

  const handleSave = async () => {
    if (!importedData || !auth.currentUser) return;

    // 檢查掃描限制
    const canScanResult = await userService.canScan(auth.currentUser.uid, auth.currentUser.email);
    if (!canScanResult.allowed) {
      alert('您的掃描次數已達上限，請聯繫 info@anivance.io 升級帳戶');
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        ...importedData,
        note: note.trim(),
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        userName: auth.currentUser.displayName || auth.currentUser.email,
        isQRImport: true,
        originalQRData: scannedData,
        timestamp: serverTimestamp()
      };

      await addDoc(collection(db, 'businessCardsnotenew'), dataToSave);
      
      // 增加掃描次數
      await userService.incrementScanCount(auth.currentUser.uid);
      
      alert('名片資料已成功匯入！');
      onClose();
    } catch (error) {
      console.error('儲存失敗:', error);
      alert('儲存失敗，請重試');
    } finally {
      setSaving(false);
    }
  };

  const resetScanner = () => {
    setScannedData(null);
    setImportedData(null);
    setError('');
    setNote('');
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>掃描 QR Code 名片</h2>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>
        
        <div className="modal-body">
          {!scannedData ? (
            <div className="scanner-section">
              <div className="scanner-container">
                <video 
                  ref={videoRef}
                  className="scanner-video"
                  style={{ display: isScanning ? 'block' : 'none' }}
                />
                
                {!isScanning && (
                  <div className="scanner-placeholder">
                    <p>點擊開始掃描按鈕來掃描 QR Code</p>
                    <p>請將 QR Code 對準攝像頭</p>
                  </div>
                )}
                
                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}
              </div>
              
              <div className="scanner-controls">
                {!isScanning ? (
                  <button onClick={startScanning} className="start-scan-btn">
                    開始掃描
                  </button>
                ) : (
                  <button onClick={stopScanning} className="stop-scan-btn">
                    停止掃描
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="result-section">
              <h3>掃描結果</h3>
              
              {importedData && (
                <div className="imported-data">
                  <h4>解析的名片資料</h4>
                  <div className="data-preview">
                    {Object.entries(importedData).map(([key, value]) => (
                      <div key={key} className="data-field">
                        <strong>{key}:</strong> <span>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="note-section">
                <label>備註:</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="添加備註..."
                  className="note-textarea"
                />
              </div>
              
              <div className="result-actions">
                <button onClick={resetScanner} className="reset-btn">
                  重新掃描
                </button>
                <button 
                  onClick={handleSave}
                  disabled={saving || !importedData}
                  className="save-btn"
                >
                  {saving ? '儲存中...' : '儲存到通訊錄'}
                </button>
              </div>
            </div>
          )}
        </div>

        <style jsx="true">{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.7);
            backdrop-filter: blur(5px);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 2000;
            padding: 20px;
          }

          .modal-content {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            border-radius: 24px;
            border: 1px solid rgba(255, 255, 255, 0.6);
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            max-width: 600px;
            width: 100%;
            max-height: 90vh;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 24px 28px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.3);
            background: rgba(255, 255, 255, 0.1);
          }

          .modal-header h2 {
            margin: 0;
            color: rgba(108, 99, 143, 0.9);
            font-size: 24px;
            font-weight: 600;
            background: linear-gradient(135deg, #3f51b5, #673ab7);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .close-btn {
            background: none;
            border: none;
            font-size: 20px;
            cursor: pointer;
            color: rgba(108, 99, 143, 0.6);
            padding: 8px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .close-btn:hover {
            background: rgba(255, 255, 255, 0.3);
            color: rgba(108, 99, 143, 0.9);
          }

          .modal-body {
            padding: 28px;
            overflow-y: auto;
            flex: 1;
          }

          .scanner-container {
            position: relative;
            width: 100%;
            height: 300px;
            background: #000;
            border-radius: 16px;
            overflow: hidden;
            margin-bottom: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .scanner-video {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .scanner-placeholder {
            text-align: center;
            color: rgba(255, 255, 255, 0.8);
            padding: 20px;
          }

          .scanner-placeholder p {
            margin: 8px 0;
            font-size: 14px;
          }

          .error-message {
            background: rgba(255, 107, 107, 0.2);
            color: rgba(180, 60, 60, 0.9);
            padding: 12px 16px;
            border-radius: 8px;
            margin: 12px 0;
            font-size: 14px;
            text-align: center;
          }

          .scanner-controls {
            display: flex;
            justify-content: center;
            margin-bottom: 20px;
          }

          .start-scan-btn, .stop-scan-btn {
            padding: 14px 32px;
            border: none;
            border-radius: 16px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .start-scan-btn {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
          }

          .start-scan-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5);
          }

          .stop-scan-btn {
            background: linear-gradient(135deg, #ff6b6b 0%, #feca57 100%);
            color: white;
            box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
          }

          .stop-scan-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(255, 107, 107, 0.5);
          }

          .result-section h3 {
            color: rgba(108, 99, 143, 0.9);
            font-size: 20px;
            font-weight: 600;
            margin-bottom: 16px;
          }

          .imported-data h4 {
            color: rgba(108, 99, 143, 0.9);
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 12px;
          }

          .data-preview {
            background: rgba(255, 255, 255, 0.3);
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.4);
            border-radius: 12px;
            padding: 16px;
            margin-bottom: 20px;
          }

          .data-field {
            padding: 8px 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          }

          .data-field:last-child {
            border-bottom: none;
          }

          .data-field strong {
            color: rgba(108, 99, 143, 0.8);
            margin-right: 8px;
            font-weight: 600;
          }

          .data-field span {
            color: rgba(108, 99, 143, 0.9);
          }

          .note-section {
            margin-bottom: 20px;
          }

          .note-section label {
            display: block;
            margin-bottom: 8px;
            color: rgba(108, 99, 143, 0.8);
            font-weight: 600;
            font-size: 14px;
          }

          .note-textarea {
            width: 100%;
            min-height: 80px;
            padding: 12px 16px;
            border: 1px solid rgba(255, 255, 255, 0.4);
            border-radius: 12px;
            background: rgba(255, 255, 255, 0.3);
            backdrop-filter: blur(10px);
            font-size: 14px;
            color: rgba(108, 99, 143, 0.9);
            font-family: inherit;
            resize: vertical;
            transition: all 0.3s ease;
            outline: none;
            box-sizing: border-box;
          }

          .note-textarea:focus {
            border-color: rgba(102, 126, 234, 0.6);
            background: rgba(255, 255, 255, 0.5);
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
          }

          .result-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
          }

          .reset-btn, .save-btn {
            padding: 12px 24px;
            border: none;
            border-radius: 12px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .reset-btn {
            background: rgba(108, 117, 125, 0.8);
            color: white;
          }

          .reset-btn:hover {
            background: rgba(108, 117, 125, 1);
            transform: translateY(-1px);
          }

          .save-btn {
            background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
            color: white;
            box-shadow: 0 4px 12px rgba(86, 171, 47, 0.3);
          }

          .save-btn:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 6px 16px rgba(86, 171, 47, 0.4);
          }

          .save-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }

          @media (max-width: 768px) {
            .modal-content {
              max-height: 95vh;
              margin: 10px;
            }

            .scanner-container {
              height: 250px;
            }

            .result-actions {
              flex-direction: column;
            }

            .reset-btn, .save-btn {
              width: 100%;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default QRScanner;